// Roles (personas) for Browse. A story's roles are derived deterministically
// from its Map products, with its topic as a fallback, plus a few content
// rules (press releases and launch posts → Executive; deprecations → Platform
// Admin). The derivation is stored on each story as `roles` (kept in sync by
// map:rebuild) so Browse can query by role. No AI is involved.
import type { Lens, Story, Topic } from "./model.ts";
import { effectiveFlag } from "./flags.ts";
import { LAUNCH_TITLE } from "./roundup.ts";

export const ROLES = [
  "data_engineer",
  "data_scientist",
  "ai_engineer",
  "data_analyst",
  "business_user",
  "executive",
  "platform_admin",
  "app_developer",
  "data_steward",
] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  data_engineer: "Data Engineer",
  data_scientist: "Data Scientist / ML",
  ai_engineer: "AI Engineer",
  data_analyst: "Data Analyst",
  business_user: "Business User",
  executive: "Executive",
  platform_admin: "Platform Admin",
  app_developer: "App Developer",
  data_steward: "Data Steward",
};

export const ROLE_HINTS: Record<Role, string> = {
  data_engineer: "Ingestion, pipelines, jobs, streaming, Delta, SQL and runtimes",
  data_scientist: "Notebooks, MLflow, model serving and foundation models",
  ai_engineer: "Agents, Agent Bricks, AI Search, Unity Gateway and model APIs",
  data_analyst: "Databricks SQL, AI/BI dashboards, Genie and metric views",
  business_user: "Genie, dashboards, Office and BI integrations, business impact",
  executive: "Press releases, launches and business impact",
  platform_admin: "Security, workspace admin, serverless, CLI/bundles, retirements",
  app_developer: "Databricks Apps, Lakebase, SDKs and Zerobus",
  data_steward: "Unity Catalog, sharing, clean rooms, data quality and metric definitions",
};

/** The summary style each role reads by default (replaces the old "Summary style" toggle). */
export const ROLE_LENS: Record<Role | "all", Lens> = {
  all: "plainEnglish",
  data_engineer: "technicalDetail",
  data_scientist: "technicalDetail",
  ai_engineer: "technicalDetail",
  data_analyst: "plainEnglish",
  business_user: "businessImpact",
  executive: "businessImpact",
  platform_admin: "technicalDetail",
  app_developer: "technicalDetail",
  data_steward: "plainEnglish",
};

/** Map product → roles that care about it. */
export const PRODUCT_ROLES: Record<string, Role[]> = {
  "lakeflow-connect": ["data_engineer"],
  "lakeflow-pipelines": ["data_engineer"],
  "lakeflow-jobs": ["data_engineer"],
  "auto-loader": ["data_engineer"],
  streaming: ["data_engineer"],
  zerobus: ["data_engineer", "app_developer"],
  "agent-bricks": ["ai_engineer", "data_scientist"],
  genie: ["data_analyst", "business_user"],
  "genie-code": ["data_engineer", "data_scientist"],
  "foundation-models": ["ai_engineer", "data_scientist"],
  "model-serving": ["data_scientist", "ai_engineer"],
  "vector-search": ["ai_engineer"],
  mlflow: ["data_scientist", "ai_engineer"],
  "ai-functions": ["data_analyst", "ai_engineer", "data_engineer"],
  "databricks-sql": ["data_analyst", "data_engineer"],
  "aibi-dashboards": ["data_analyst", "business_user"],
  "metric-views": ["data_analyst", "data_steward"],
  "bi-integrations": ["data_analyst", "business_user"],
  "databricks-apps": ["app_developer"],
  lakebase: ["app_developer", "data_engineer"],
  "unity-catalog": ["data_steward", "platform_admin"],
  "unity-gateway": ["ai_engineer", "platform_admin"],
  "delta-sharing": ["data_steward", "data_engineer"],
  "clean-rooms": ["data_steward"],
  "data-quality": ["data_steward", "data_engineer"],
  serverless: ["platform_admin", "data_engineer"],
  runtime: ["data_engineer", "data_scientist", "platform_admin"],
  notebooks: ["data_engineer", "data_scientist"],
  "developer-tools": ["app_developer", "platform_admin", "data_engineer"],
  "security-admin": ["platform_admin"],
  "delta-lake": ["data_engineer"],
};

/** Used only when a story has no Map product. */
export const TOPIC_ROLES: Record<Topic, Role[]> = {
  ai_agents: ["ai_engineer", "data_scientist"],
  data_engineering: ["data_engineer"],
  analytics_bi: ["data_analyst", "business_user"],
  governance: ["data_steward", "platform_admin"],
  platform: ["platform_admin"],
  business_impact: ["executive", "business_user"],
};

export function deriveRoles(s: Story): Role[] {
  const out = new Set<Role>();
  const fromProducts = s.products.flatMap((p) => PRODUCT_ROLES[p] ?? []);
  for (const r of fromProducts.length ? fromProducts : TOPIC_ROLES[s.primaryTopic] ?? []) out.add(r);
  if (s.contentType === "announcement") out.add("executive");
  if (s.contentType === "blog" && LAUNCH_TITLE.test(s.title)) out.add("executive");
  if (s.primaryTopic === "business_impact") (out.add("executive"), out.add("business_user"));
  const flag = effectiveFlag(s)?.status;
  if (flag === "deprecated" || flag === "retired") out.add("platform_admin");
  return ROLES.filter((r) => out.has(r));
}

/** Stored roles when present (Firestore), otherwise derived (bundled samples, older docs). */
export function storyRoles(s: Story): Role[] {
  return s.roles ?? deriveRoles(s);
}

export const sameRoles = (a: readonly string[] | undefined, b: readonly string[]) => !!a && a.length === b.length && a.every((x, i) => x === b[i]);
